import { FC, lazy, Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ChartErrorState from "./ChartErrorState";
import {
  resolveState,
  isRegionResponse,
  RANK_RAMP,
  excludedShare,
  shouldShowExcludedNotice,
  shouldWarnIgnoredFilters,
  type RegionResponse,
} from "./regionMapOptions";
import "./EmissionsByRegionMap.scss";

/**
 * deck.gl lives behind a lazy import so its ~330KB gzipped never reaches the main
 * bundle — measured: importing it directly took the app from 893KB to 1.22MB, while
 * lazy-loading leaves the main bundle untouched and puts deck.gl in a 333KB chunk
 * fetched only when someone opens this sub-tab.
 */
const RegionDeckMap = lazy(() => import("./RegionDeckMap"));

interface EmissionsByRegionMapProps {
  data: RegionResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const EmissionsByRegionMap: FC<EmissionsByRegionMapProps> = ({
  data,
  isLoading,
  error,
  onRetry,
}) => {
  const { t } = useTranslation();

  /**
   * Validated once, here, rather than re-guarded at every read below. `data` is
   * typed as `RegionResponse` but that is only an axios cast — nothing enforces
   * it on the wire — so a malformed payload (wrong endpoint, stale mock, future
   * shape change) must not reach any of the reads further down as if it were a
   * real response. `resolveState` applies the same check for `cells`/`flows`-
   * shaped state; this is what keeps `coverage`/`totals`/`ignoredFilters` reads
   * in the captions below safe too, since those run outside the "map" state.
   */
  const safeData = useMemo(
    () => (data && isRegionResponse(data) ? data : null),
    [data]
  );

  const state = useMemo(
    () => resolveState(safeData, isLoading, error),
    [safeData, isLoading, error]
  );

  /**
   * What the CURRENT zoom is drawing. The server's own figures describe its finest
   * resolution, so once detail follows the zoom those numbers stop matching the
   * picture — a caption claiming "60 routes" over a map showing four is worse than
   * no caption at all.
   */
  /**
   * Layer visibility and view reset.
   *
   * Zoom, fullscreen and the scale bar are deck.gl's own widgets, drawn on the
   * canvas. These three are product decisions rather than navigation, so they live
   * in ordinary markup where they are keyboard reachable and screen-reader visible.
   */
  const [showRoutes, setShowRoutes] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  /**
   * On by default, at the product owner's request.
   *
   * It was off: tiles are the only thing on this chart that reach a third party,
   * and the request discloses roughly where a tenant's freight moves. That
   * trade-off has been made deliberately in favour of the map being legible on
   * arrival — most readers cannot orient themselves on administrative regions
   * alone. The toggle still turns it off, and the attribution still appears and
   * disappears with the tiles.
   */
  const [showBasemap, setShowBasemap] = useState(true);
  const [resetToken, setResetToken] = useState(0);

  interface MapDetail {
    regionsShown: number;
    flowsShown: number;
    flowsTotal: number;
    /** Share of the DRAWABLE routes' emissions that the drawn arcs carry. */
    flowSharePct: number;
    /** Share of the whole selection's emissions those same arcs carry. */
    totalSharePct: number;
  }
  const [detail, setDetail] = useState<MapDetail | null>(null);
  const onDetailChange = useCallback((d: MapDetail) => setDetail(d), []);

  const captions = (
    <>
      {state.kind === "map" && showBasemap && (
        <p className="region-map__caption region-map__caption--muted">
          {t(
            "cpi.regionMap.basemapAttribution",
            "Street map \u00a9 Esri, HERE, Garmin, \u00a9 OpenStreetMap contributors"
          )}
        </p>
      )}
      {/* TWO denominators, because they answer different questions and the gap
          between them is the point. The first is of DRAWABLE route emissions —
          "100%" there means every route that can be drawn is on screen, not that
          the map accounts for everything. The second is of the selection's total,
          which is always the larger denominator: a route needs BOTH a pickup and a
          delivery coordinate, so routes cover fewer shipments than the shaded
          regions do. Naming only the first would claim coverage it cannot support.
          The drawn/total counts were dropped at the product owner's request. */}
      {state.kind === "map" && (detail?.flowsTotal ?? 0) > 0 && (
        <p className="region-map__caption">
          {t(
            "cpi.regionMap.flowCoverage",
            "Showing {{pct}}% of route emissions ({{totalPct}}% of all emissions in the above selected filters). Zoom in and out for more and fewer routes.",
            {
              pct: (detail?.flowSharePct ?? 0).toFixed(0),
              totalPct: (detail?.totalSharePct ?? 0).toFixed(0),
            }
          )}
        </p>
      )}
      {shouldWarnIgnoredFilters(safeData) && (
        <p className="region-map__warning" role="status">
          {t(
            "cpi.regionMap.ignoredFilters",
            "This map cannot apply these filters: {{filters}}. Other charts on this page do."
          , { filters: (safeData?.ignoredFilters ?? []).join(", ") })}
        </p>
      )}
      {safeData && shouldShowExcludedNotice(safeData.coverage) && (
        <p className="region-map__caption">
          {t(
            "cpi.regionMap.excludedNotice",
            "{{shipments}} shipments ({{pct}}%) have no usable pickup location and are not shown.",
            {
              shipments: safeData.coverage.excludedNoGeo.toLocaleString(),
              pct: excludedShare(safeData.coverage).toFixed(1),
            }
          )}
        </p>
      )}
      {/* Two captions were removed at the product owner's request: the
          outside-any-region notice (the no-usable-pickup notice above it stays, so
          the larger of the two exclusions is still disclosed), and the summary's
          refresh timestamp. The latter was MAX(last_refreshed_at) over the region
          summary's partitions — when that derived aggregate was last rebuilt, not
          when the tenant's data arrived — so it read as a freshness guarantee it
          never made. `lastRefreshedAt` is still on the response; nothing shows it. */}
    </>
  );

  return (
    <section className="region-map">
      {/* No heading or strapline: the sub-tab already names this section, so both
          were repeating it. What the two layers mean is carried by the legend —
          the rank ramp for the shaded regions, the endpoint key for the routes. */}
      {state.kind === "loading" && (
        <div className="region-map__skeleton" aria-busy="true" />
      )}

      {state.kind === "error" && <ChartErrorState onRetry={onRetry} />}

      {state.kind === "empty" && (
        <p className="region-map__empty" role="status">
          {t(
            "cpi.regionMap.empty",
            "No pickup locations could be placed on a map for the above selected filters."
          )}
        </p>
      )}

      {state.kind === "single" && (
        <p className="region-map__empty" role="status">
          {t(
            "cpi.regionMap.singleRegion",
            "All emissions in the above selected filters fall in one region, so there is nothing to compare on a map."
          )}
        </p>
      )}

      {state.kind === "map" && (
        <>
          <div className="region-map__toolbar">
            <button
              type="button"
              className="region-map__tool"
              aria-pressed={showRoutes}
              onClick={() => setShowRoutes((v) => !v)}
            >
              {t("cpi.regionMap.toggleRoutes", "Routes")}
            </button>
            <button
              type="button"
              className="region-map__tool"
              aria-pressed={showLabels}
              onClick={() => setShowLabels((v) => !v)}
            >
              {t("cpi.regionMap.toggleLabels", "Labels")}
            </button>
            <button
              type="button"
              className="region-map__tool"
              aria-pressed={showBasemap}
              onClick={() => setShowBasemap((v) => !v)}
            >
              {t("cpi.regionMap.toggleBasemap", "Street map")}
            </button>
            <button
              type="button"
              className="region-map__tool"
              onClick={() => setResetToken((n) => n + 1)}
            >
              {t("cpi.regionMap.resetView", "Fit to data")}
            </button>
          </div>
          <div className="region-map__chart">
            <Suspense fallback={<div className="region-map__skeleton" aria-busy="true" />}>
              <RegionDeckMap
                showRoutes={showRoutes}
                showLabels={showLabels}
                showBasemap={showBasemap}
                resetToken={resetToken}
                onDetailChange={onDetailChange}
                cells={state.cells}
                flows={safeData?.flows ?? []}
                totalKg={safeData?.totals.emissionWtwKg ?? 0}
                unnamedFallback={t("cpi.regionMap.unnamedRegion", "Unnamed area")}
                unlabelledRegion={(id) =>
                  t("cpi.regionMap.unlabelledRegion", "Region {{id}}", { id })
                }
                labels={{
                  emissions: t("cpi.regionMap.tooltipEmissions", "Emissions"),
                  intensity: t("cpi.regionMap.tooltipIntensity", "Intensity"),
                  intensityUnknown: t(
                    "cpi.regionMap.tooltipIntensityUnknown",
                    "Intensity: not available"
                  ),
                  shipments: t("cpi.regionMap.tooltipShipments", "Shipments"),
                  pickups: t("cpi.regionMap.tooltipPickups", "Picked up in"),
                  morePickups: (n: number) =>
                    t("cpi.regionMap.tooltipMorePickups", "and {{places}} more", {
                      // "count" is reserved by i18next for pluralisation rules.
                      places: n,
                    }),
                }}
              />
            </Suspense>
          </div>
          {/* The fill still encodes each region's RANK among the others rather than
              its magnitude — real tenants are skewed enough that a magnitude ramp
              leaves most regions indistinguishable — but the parenthetical saying
              so was removed as noise. The values are in the tooltip. Reads across
              as one phrase: "Lowest [ramp] Highest emissions". */}
          <div className="region-map__legend">
            <span className="region-map__legend-label">
              {t("cpi.regionMap.legendLow", "Lowest")}
            </span>
            <span className="region-map__legend-swatches" aria-hidden="true">
              {RANK_RAMP.map((c) => (
                <i key={c} style={{ background: c }} />
              ))}
            </span>
            <span className="region-map__legend-label">
              {t("cpi.regionMap.legendHigh", "Highest emissions")}
            </span>

            {/* Which end of a route is which. The arcs encode it as a pale-to-
                saturated gradient, which a reader who cannot separate the two hues
                never sees; the map therefore also marks the ends by SHAPE, and this
                is the key to those shapes. Hidden with the routes themselves. */}
            {showRoutes && (detail?.flowsShown ?? 0) > 0 && (
              <span className="region-map__legend-endpoints">
                <span className="region-map__legend-endpoint">
                  <i
                    className="region-map__legend-marker region-map__legend-marker--departure"
                    aria-hidden="true"
                  />
                  {t("cpi.regionMap.legendDeparture", "Departure")}
                </span>
                <span className="region-map__legend-endpoint">
                  <i
                    className="region-map__legend-marker region-map__legend-marker--arrival"
                    aria-hidden="true"
                  />
                  {t("cpi.regionMap.legendArrival", "Arrival")}
                </span>
              </span>
            )}
          </div>
        </>
      )}

      {captions}
    </section>
  );
};

export default EmissionsByRegionMap;
